import * as Sentry from '@sentry/react';

/**
 * Supabase/PostgREST failures arrive as a plain object — `{ code, details,
 * hint, message }` — not an Error.
 *
 * Two consequences, both of which made the comms errors undiagnosable:
 *
 *  - `Sentry.captureException(plainObject)` renders as
 *    "Object captured as exception with keys: code, details, hint, message"
 *    and every field that identifies the cause is discarded.
 *  - `throw plainObject` inside a react-query `queryFn` gives the rejection no
 *    name either, so those issues group under the callback name
 *    ("useQuery$argument_0.queryFn") with nothing to distinguish them.
 *
 * In tenant-admin this is TENANT-ADMIN-PROD-79 (109 events / 46 users) and
 * friends, none of which could be acted on.
 */
export interface SupabaseErrorLike {
  code?: string | null;
  message?: string | null;
  details?: string | null;
  hint?: string | null;
}

/** Best-effort read of the PostgREST fields off an unknown caught value. */
function fields(error: unknown): SupabaseErrorLike {
  if (!error || typeof error !== 'object') return {};
  const e = error as SupabaseErrorLike;
  return {
    code: typeof e.code === 'string' ? e.code : undefined,
    message: typeof e.message === 'string' ? e.message : undefined,
    details: typeof e.details === 'string' ? e.details : undefined,
    hint: typeof e.hint === 'string' ? e.hint : undefined,
  };
}

/** PostgREST's expired/invalid-JWT signals. Recoverable, not defects. */
export function isSupabaseAuthError(error: unknown): boolean {
  const { code, message } = fields(error);
  if (code === 'PGRST301' || code === '401') return true;
  const msg = (message ?? '').toLowerCase();
  return msg.includes('jwt') || msg.includes('unauthorized');
}

/**
 * Build a real Error that keeps the payload in both the name and the message,
 * so the Sentry title reads e.g.
 *   SupabaseError(PGRST301): fetch_messages — JWT expired
 *
 * A value that is already an Error is returned untouched — it has a stack, and
 * rewrapping would throw that away.
 */
export function toSupabaseError(error: unknown, action: string): Error {
  if (error instanceof Error) return error;

  const { code, message } = fields(error);
  const err = new Error(`${action} — ${message ?? 'unknown error'}`);
  err.name = `SupabaseError${code ? `(${code})` : ''}`;
  return err;
}

/**
 * Report a Supabase failure with its fields intact and return the Error to
 * throw, so a single call site does both:
 *
 *   if (error) throw captureSupabaseError(error, { action: 'fetch_messages' });
 */
export function captureSupabaseError(
  error: unknown,
  { action, extra }: { action: string; extra?: Record<string, unknown> },
): Error {
  const reportable = toSupabaseError(error, action);
  const { code, message, details, hint } = fields(error);
  Sentry.captureException(reportable, {
    tags: { feature: 'comms', action },
    extra: {
      ...extra,
      code: code ?? null,
      message: message ?? (error instanceof Error ? error.message : null),
      details: details ?? null,
      hint: hint ?? null,
    },
  });
  return reportable;
}
